import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Pagination,
  Spinner,
  Table,
} from "react-bootstrap";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import nodeApi from "../lib/api";

export default function Site() {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    address: "",
  });
  const [editId, setEditId] = useState(null);
  const [sortedSites, setSortedSites] = useState([]);
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const challansPerPage = 10;

  // Fetch site details
  const {
    data: sites = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await nodeApi.get("/site");
      // console.log("Fetched sites:", res.data);
      return res.data.sites;
    },
    onError: (err) => {
      toast.error(`Failed to fetch sites: ${err.message}`);
    },
  });

  // Sort sites by created_at in descending order
  useEffect(() => {
    //   console.log("Sites from query:", sites);

    if (sites.length > 0) {
      let filtered = [...sites];

      // Apply search filter
      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        filtered = filtered.filter(
          (site) =>
            site.id?.toString().includes(term) ||
            site.name?.toLowerCase().includes(term) ||
            site.address?.toLowerCase().includes(term)
        );
        setSortedSites(filtered);
        return;
      }
      const sorted = [...sites].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        if (isNaN(dateA)) return 1;
        if (isNaN(dateB)) return -1;
        return dateB - dateA; // Descending order
      });
      setSortedSites(sorted);
    } else {
      setSortedSites([]);
    }
  }, [searchTerm, sites]);

  // Create or update site mutation
  const mutation = useMutation({
    mutationFn: async (data) => {
      const payload = {
        name: data?.name?.trim(),
      };

      if (editId) {
        payload.address = data?.address?.trim(); // Always send address during edit
      } else if (data?.address?.trim()) {
        payload.address = data?.address?.trim();
      }

      if (editId) {
        return nodeApi.put(`/site/${editId}`, payload);
      } else {
        return nodeApi.post("/site", payload);
      }
    },
    onSuccess: () => {
      toast.success(`Site ${editId ? "updated" : "added"} successfully`);
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      handleClose();
    },
    onError: (err) => {
      console.log(err);
      toast.error(err.response?.data?.message || "Error saving site");
    },
  });

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Prevent non-digit input for number field
    if (name === "number") {
      if (!/^\d*$/.test(value)) {
        return;
      }
    }

    setForm({ ...form, [name]: value });
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();

    // Required field validation
    if (!form.name) {
      toast.error("Site Name is required.");
      return;
    }

    mutation.mutate(form);
  };

  // Handle edit
  const handleEdit = (site) => {
    setForm({
      name: site.name,
      address: site.address,
    });
    setEditId(site.id);
    setShowModal(true);
  };

  // Handle modal close
  const handleClose = () => {
    setForm({ name: "", address: "" });
    setEditId(null);
    setShowModal(false);
  };

  const indexOfLast = currentPage * challansPerPage;
  const indexOfFirst = indexOfLast - challansPerPage;
  const currentChallans = sortedSites.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedSites.length / challansPerPage);
  let pages = [];
  if (totalPages > 1) {
    pages.push(1);
    if (currentPage > 3) {
      pages.push("dots-left");
    }
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p > 1 && p < totalPages) {
        pages.push(p);
      }
    }
    if (currentPage < totalPages - 2) {
      pages.push("dots-right");
    }
    pages.push(totalPages);
  }

  return (
    <div className="d-flex flex-column vh-100" >
      <Navbar />
      <div className="d-flex flex-grow-1 overflow-hidden">
        <Sidebar />
        <Container fluid className="flex-grow-1 d-flex flex-column p-4 overflow-hidden">
          <Row className="mb-3">
            <Col>
              <h4 className="fw-bold">Sites</h4>
            </Col>
            
          </Row>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search Sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col className="text-end">
              <Button variant="primary" onClick={() => setShowModal(true)}>
                Add Site
              </Button>
            </Col>
          </Row>
          <style>
            {`
             .col-site { width: 120px; }      
             .col-name { width: 200px; }      
             .col-address { width: 300px; }   
             .col-actions { width: 150px; } `}
          </style>

          {/* Site Table */}
          <div className="table-responsive flex-grow-1 overflow-auto">
          <Table bordered hover className="mb-0" >
            <thead className="table-secondary sticky-top ">
              <tr>
                 <th className="col-site">Site ID</th>
                 <th className="col-name">Name</th>
                 <th className="col-address">Address</th>
                 <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    <Spinner animation="border" />
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan="6" className="text-center text-danger">
                    Error: {error.message}
                  </td>
                </tr>
              ) : currentChallans.length > 0 ? (
                currentChallans.map((s) => (
                  <tr key={s.id}>
                    <td>{s.id || "-"}</td>
                    <td>{s.name || "-"}</td>
                    <td>{s.address || "-"}</td>
                    <td>
                      <Button
                        variant="dark"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(s)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No sites found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-3"
            style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
              <ul className="pagination justify-content-center">
                <Pagination.Prev
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </Pagination.Prev>
                {pages.map((p, idx) => (
                  <li
                    key={idx}
                    className={`page-item ${
                      p === currentPage ? "active" : ""
                    } ${
                      p === "dots-left" || p === "dots-right" ? "disabled" : ""
                    }`}
                  >
                    {p === "dots-left" || p === "dots-right" ? (
                      <span className="page-link">…</span>
                    ) : (
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    )}
                  </li>
                ))}
                <Pagination.Next
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
              </ul>
            </nav>
          )}

          {/* Create/Edit Modal */}
          <Modal show={showModal} onHide={handleClose} centered>
            <Modal.Header closeButton>
              <Modal.Title>{editId ? "Edit Site" : "Add Site"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Site Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    onBlur={() =>
                      setForm((form) => ({
                        ...form,
                        name: form.name,
                      }))
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                  />
                </Form.Group>
                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={mutation.isPending}
                  >
                    {mutation.isPending
                      ? "Saving..."
                      : editId
                      ? "Update Site"
                      : "Add Site"}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </div>
  );
}
